import { Loader2Icon } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

import { AddParticipantMemberItem } from './add-participant-member-item.atom'

export interface SelectableMember {
  id: string
  name: string
  avatarUrl?: string
}

export interface AddParticipantMemberListProps {
  memberList: SelectableMember[]
  onMemberSelect: (userId: string) => void
  isLoading?: boolean
  className?: string
}

/**
 * A searchable list of organization members for adding to a conversation.
 * Uses Command component for keyboard navigation and filtering.
 */
export const AddParticipantMemberList: React.FC<AddParticipantMemberListProps> = ({
  memberList,
  onMemberSelect,
  isLoading = false,
  className,
}): ReactNode => (
  <Command className={cn('add-participant-member-list', className)}>
    <CommandInput placeholder="Search members..." className="add-participant-search-input" />
    <CommandList className="add-participant-list max-h-72">
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {!isLoading && <CommandEmpty>No members found.</CommandEmpty>}
      {!isLoading && memberList.length > 0 && (
        <CommandGroup>
          {memberList.map((member) => (
            <AddParticipantMemberItem
              key={member.id}
              id={member.id}
              name={member.name}
              avatarUrl={member.avatarUrl}
              onSelect={onMemberSelect}
            />
          ))}
        </CommandGroup>
      )}
    </CommandList>
  </Command>
)

AddParticipantMemberList.displayName = 'AddParticipantMemberList'
